import type {
	SectorData,
	SectorTypePlant,
	PlantPermission,
	RoleObjToAddItem,
	RoleObjToRemoveItem,
	PermissionListItem,
} from "models/dto/user";
import type { SectorAdminRow, PlantUserRow } from "../types";

/** Serialize permissions for equality comparison (sorted by businessObjectId only). */
function permissionsKey(permissions: PlantPermission[]): string {
	const sorted = [...(permissions || [])].sort((a, b) =>
		(a.businessObjectId || "").localeCompare(b.businessObjectId || "")
	);
	return JSON.stringify(sorted.map((p) => ({ businessObjectId: p.businessObjectId })));
}

/**
 * Compute delta for UpdatePlantUserRequest: sectorsToAdd (new or changed sector/plant/permissions)
 * and sectorsToRemove (sector/plant removed). Backend applies removes first, then adds.
 */
export function ComputePlantUserSectorDelta(
	initial: Record<string, SectorData> | null,
	current: SectorData[]
): { sectorsToAdd: SectorData[]; sectorsToRemove: SectorData[] } {
	const delim = "\u0001";
	const initialMap = new Map<string, { permissions: PlantPermission[] }>();
	if (initial) {
		for (const [sectorId, sector] of Object.entries(initial)) {
			for (const plant of sector.plants || []) {
				if (!plant.plantId) continue;
				initialMap.set(`${sectorId}${delim}${plant.plantId}`, {
					permissions: plant.permissions || [],
				});
			}
		}
	}
	const currentMap = new Map<string, { sectorId: string; plant: SectorTypePlant }>();
	for (const sector of current) {
		for (const plant of sector.plants || []) {
			if (!plant.plantId) continue;
			const key = `${sector.sectorId}${delim}${plant.plantId}`;
			currentMap.set(key, { sectorId: sector.sectorId, plant });
		}
	}

	// sectorsToRemove: in initial but not in current (or plant removed)
	const removeBySector = new Map<string, { plantId: string }[]>();
	for (const [key, { permissions }] of initialMap) {
		const currentEntry = currentMap.get(key);
		const stillInCurrent =
			currentEntry &&
			(currentEntry.plant.permissions?.length ?? 0) > 0;
		if (!stillInCurrent) {
			const [sectorId, plantId] = key.split(delim);
			const list = removeBySector.get(sectorId) ?? [];
			list.push({ plantId });
			removeBySector.set(sectorId, list);
		}
	}
	const sectorsToRemove: SectorData[] = [];
	for (const [sectorId, plants] of removeBySector) {
		sectorsToRemove.push({
			sectorId,
			plants: plants.map((p) => ({ plantId: p.plantId, permissions: [] })),
		});
	}

	// sectorsToAdd: in current but not in initial, or permissions changed
	const addBySector = new Map<string, SectorTypePlant[]>();
	for (const [, { sectorId, plant }] of currentMap) {
		if ((plant.permissions?.length ?? 0) === 0) continue;
		const key = `${sectorId}${delim}${plant.plantId}`;
		const initialEntry = initialMap.get(key);
		const initialPerms = initialEntry?.permissions ?? null;
		const currentPerms = plant.permissions || [];
		const same =
			initialPerms !== null &&
			permissionsKey(initialPerms) === permissionsKey(currentPerms);
		if (!same) {
			const list = addBySector.get(sectorId) ?? [];
			list.push({ plantId: plant.plantId, permissions: currentPerms });
			addBySector.set(sectorId, list);
		}
	}
	const sectorsToAdd: SectorData[] = [];
	for (const [sectorId, plants] of addBySector) {
		sectorsToAdd.push({ sectorId, plants });
	}

	return { sectorsToAdd, sectorsToRemove };
}

/** Set of objectIds from permissions. */
function objectIdsSet(permissions: PlantPermission[]): Set<string> {
	const set = new Set<string>();
	for (const p of permissions || []) {
		if (p.businessObjectId) set.add(p.businessObjectId);
	}
	return set;
}

/**
 * Build roleObjToAdd and roleObjToRemove for PUT plant-user. All items use sectorId + plantId.
 * Add: only permissions being added (merge). Remove: only specified objectIds (never send empty permissionList).
 * Backend skips empty permissionList; after removing grants it disables the role if none remain.
 */
export function ComputeRoleObjDelta(
	initial: Record<string, SectorData> | null,
	current: Record<string, SectorData>
): { roleObjToAdd: RoleObjToAddItem[]; roleObjToRemove: RoleObjToRemoveItem[] } {
	const roleObjToAdd: RoleObjToAddItem[] = [];
	const roleObjToRemove: RoleObjToRemoveItem[] = [];
	const delim = "\u0001";

	const initialByKey = new Map<string, { sectorId: string; plantId: string; permissions: PlantPermission[] }>();
	if (initial) {
		for (const [sectorId, sector] of Object.entries(initial)) {
			for (const plant of sector.plants || []) {
				if (!plant.plantId) continue;
				initialByKey.set(`${sectorId}${delim}${plant.plantId}`, {
					sectorId,
					plantId: plant.plantId,
					permissions: plant.permissions || [],
				});
			}
		}
	}

	const currentByKey = new Map<string, { sectorId: string; plant: SectorTypePlant }>();
	for (const [sectorId, sector] of Object.entries(current)) {
		for (const plant of sector.plants || []) {
			if (!plant.plantId) continue;
			currentByKey.set(`${sectorId}${delim}${plant.plantId}`, { sectorId, plant });
		}
	}

	// Remove: (1) entire assignment — plant gone or has no permissions: send all initial objectIds so backend removes all grants, then disables role
	for (const [key, { sectorId, plantId, permissions: initialPerms }] of initialByKey) {
		const cur = currentByKey.get(key);
		const stillHasPermissions = cur && (cur.plant.permissions?.length ?? 0) > 0;
		if (!stillHasPermissions) {
			const toRemove = (initialPerms || [])
				.filter((p) => p.businessObjectId)
				.map((p) => ({ objectId: p.businessObjectId! }));
			if (toRemove.length > 0) {
				roleObjToRemove.push({ sectorId, plantId, permissionList: toRemove });
			}
		}
	}

	// Remove: (2) specific permissions removed — in both but some objectIds dropped
	for (const [key, { sectorId, plantId, permissions: initialPerms }] of initialByKey) {
		const cur = currentByKey.get(key);
		if (!cur || (cur.plant.permissions?.length ?? 0) === 0) continue;
		const initialIds = objectIdsSet(initialPerms);
		const currentIds = objectIdsSet(cur.plant.permissions || []);
		const removedIds: string[] = [];
		for (const id of initialIds) {
			if (!currentIds.has(id)) removedIds.push(id);
		}
		if (removedIds.length > 0) {
			roleObjToRemove.push({
				sectorId,
				plantId,
				permissionList: removedIds.map((objectId) => ({ objectId })),
			});
		}
	}

	// Add: only permissions being added (merge) — objectIds in current but not in initial
	for (const [, { sectorId, plant }] of currentByKey) {
		const currentPerms = plant.permissions || [];
		if (currentPerms.length === 0) continue;
		const key = `${sectorId}${delim}${plant.plantId}`;
		const initialEntry = initialByKey.get(key);
		const initialIds = initialEntry ? objectIdsSet(initialEntry.permissions) : new Set<string>();
		const currentIds = objectIdsSet(currentPerms);
		const addedIds: string[] = [];
		for (const id of currentIds) {
			if (!initialIds.has(id)) addedIds.push(id);
		}
		if (addedIds.length > 0) {
			roleObjToAdd.push({
				sectorId,
				plantId: plant.plantId,
				permissionList: addedIds.map((objectId) => ({ objectId })),
			});
		}
	}

	return { roleObjToAdd, roleObjToRemove };
}

/** Sector with id and name (e.g. AccessibleSector). */
type sectorWithName = { id: string; name: string };

/** Plant with id and name (e.g. AccessiblePlant). */
type plantWithName = { id: string; name: string };

export interface IncompleteSectorsWarningResult {
	/** Sector names that have no plants or at least one plant with no permissions (for disabling Save). */
	omittedSectorNames: string[];
	/** Sectors that have at least one plant with no permissions: sector name and list of those plant names. */
	plantsWithoutPermissionsBySector: { sectorName: string; plantNames: string[] }[];
	/** Sector names that have zero plants. */
	sectorsWithNoPlantsNames: string[];
}

/**
 * Identifies sectors with incomplete permissions: no plants or any plant has no permissions.
 * These sectors will be omitted from the save payload (same logic as submit).
 * Always returns sector- and plant-level detail; when sectorTypePlants is provided, plant names
 * are shown instead of plant IDs for clearer messaging.
 */
export function GetIncompleteSectorsWarning(
	sectorData: Record<string, SectorData>,
	accessibleSectors: sectorWithName[],
	sectorTypePlants?: Record<string, plantWithName[]>
): IncompleteSectorsWarningResult {
	const omittedSectorIds = Object.entries(sectorData)
		.filter(
			([, s]) =>
				s.plants.length === 0 ||
				s.plants.some((p) => (p.permissions?.length ?? 0) === 0)
		)
		.map(([id]) => id);
	const omittedSectorNames = omittedSectorIds
		.map((id) => accessibleSectors.find((s) => s.id === id)?.name ?? id)
		.filter(Boolean);

	const plantsWithoutPermissionsBySector: { sectorName: string; plantNames: string[] }[] = [];
	const sectorsWithNoPlantsNames: string[] = [];

	for (const [sectorId, data] of Object.entries(sectorData)) {
		const sectorName = accessibleSectors.find((s) => s.id === sectorId)?.name ?? sectorId;
		if (data.plants.length === 0) {
			if (omittedSectorIds.includes(sectorId)) {
				sectorsWithNoPlantsNames.push(sectorName);
			}
			continue;
		}
		const plantsWithNoPerms = data.plants.filter(
			(p) => (p.permissions?.length ?? 0) === 0
		);
		if (plantsWithNoPerms.length === 0) continue;
		const plantsForSector = sectorTypePlants?.[sectorId] ?? [];
		const plantNames = plantsWithNoPerms.map((p) => {
			const found = plantsForSector.find((pl) => pl.id === p.plantId);
			return found?.name ?? p.plantId;
		});
		plantsWithoutPermissionsBySector.push({ sectorName, plantNames });
	}

	return {
		omittedSectorNames,
		plantsWithoutPermissionsBySector,
		sectorsWithNoPlantsNames,
	};
}

/**
 * Message for the incomplete-permissions warning (Add/Edit Plant User).
 * Guides the user to add permissions to plants or remove those plants (not delete the sector).
 */
export function GetIncompleteSectorsWarningMessage(warning: IncompleteSectorsWarningResult): string {
	const parts: string[] = [];
	for (const { sectorName, plantNames } of warning.plantsWithoutPermissionsBySector) {
		const list = plantNames.join(", ");
		if (plantNames.length === 1) {
			parts.push(
				`${list} in the ${sectorName} sector has no permissions. Add at least one permission to this plant or remove the plant before saving.`
			);
		} else {
			parts.push(
				`Some plants in the ${sectorName} sector have no permissions and won't be saved: ${list}. Add at least one permission to each listed plant or remove those plants before saving.`
			);
		}
	}
	if (warning.sectorsWithNoPlantsNames.length > 0) {
		const list = warning.sectorsWithNoPlantsNames.join(", ");
		parts.push(
			`The following sector(s) have no plants and won't be saved: ${list}. Add plants or remove the sector before saving.`
		);
	}
	return parts.join(" ");
}

/**
 * Returns true if, after the planned change, the user would have no access to MCC.
 * Used by Edit Sector Admin and Edit Plant User to choose confirmation message and button style.
 *
 * @param fullUserSectorIds - All sector IDs the user is in (any role) from user context
 * @param removedSectorIds - Sector IDs being removed by this action
 * @param savedSectorCount - Number of sectors they will have after save (for the role being edited)
 */
export function WouldUserHaveNoAccessAfterChange(
	fullUserSectorIds: string[] | null | undefined,
	removedSectorIds: string[],
	savedSectorCount: number
): boolean {
	if (savedSectorCount > 0) return false;
	const ids = fullUserSectorIds?.filter(Boolean) ?? [];
	if (ids.length === 0) return false;
	const removedSet = new Set(removedSectorIds);
	return ids.every((id) => removedSet.has(id));
}

/**
 * Builds the "already has access" error message for Add Plant User when the user
 * already exists as Sector Admin and/or Plant User in overlapping sectors.
 */
export function BuildAlreadyHasAccessMessage(
	gmin: string,
	sectorAdmins: SectorAdminRow[],
	plantUsers: PlantUserRow[]
): string {
	const saRow = sectorAdmins.find((r) => r.userId === gmin);
	const puRows = (plantUsers || []).filter((r) => r.userId === gmin);
	const saSectors = saRow?.sectors?.length
		? saRow.sectors.sort((a, b) => a.localeCompare(b)).join(", ")
		: "";
	const puSectors = puRows.length
		? [...new Set(puRows.map((r) => r.sector))].sort((a, b) => a.localeCompare(b)).join(", ")
		: "";
	const parts: string[] = [];
	if (saSectors) {
		parts.push(
			`This user is already a Sector Admin for ${saSectors}. To add or change their sectors, find their row in the Sector Admins table and click Edit.`
		);
	}
	if (puSectors) {
		parts.push(
			`This user is already a Plant User in ${puSectors}. To change their plants or sector, find their row in the Plant Users table and click Edit.`
		);
	}
	if (parts.length > 0) return parts.join(" ");
	return "This user already has access in MCC. To change their sectors or plants, find their row in the Sector Admins or Plant Users table and click Edit.";
}
